import { createElement, Fragment, type ReactNode } from 'react';
import { type AttachmentsByExternalId, getAttachmentExternalIdFromAlt } from '../model/attachments';
import {
  type BrokenContentImage,
  getBrokenContentImage,
  getElementProps,
  isElementNode,
  isTableLayoutWhitespace,
  isVoidElement,
} from '../model/htmlContent';
import type { LightboxImage } from './AttachmentLightbox';
import { ContentImage } from './ContentImage';
import { MessageAttachmentView } from './MessageAttachmentView';

/** Как показывать разобранное оформленное содержимое */
type ContentNodesOptions = {
  /** Вложения сообщения — на место картинки с подписью `ATTACHMENT:{id}` встаёт вложение */
  attachmentsByExternalId: AttachmentsByExternalId;
  /** Ключи не загрузившихся картинок: на своём месте они не показываются */
  brokenImageKeys: ReadonlySet<string>;
  /** Блок пересланного письма и то, что показывается вместо него и всего, что идёт за ним */
  forwarded: { block: Element; node: ReactNode } | null;
  /** Открыть картинку увеличенной */
  onImageOpen: (image: LightboxImage) => void;
  /** Картинка не загрузилась */
  onImageError: (image: BrokenContentImage) => void;
};

/**
 * Переносит разобранное оформленное содержимое в элементы React — оформление показывается, разметка
 * на экран не попадает. Картинки и пересланное письмо при этом становятся своими компонентами,
 * а не вставкой чужой разметки на страницу.
 *
 * Ключи картинок — их порядок в содержимом: при каждом показе одного и того же содержимого
 * они одни и те же, поэтому не загрузившаяся картинка узнаётся и после перерисовки.
 */
export const renderContentNodes = (root: Element, options: ContentNodesOptions): ReactNode[] => {
  const { attachmentsByExternalId, brokenImageKeys, forwarded, onImageOpen, onImageError } = options;
  let imageIndex = 0;

  const renderImage = (image: Element, key: number): ReactNode => {
    const imageKey = `image-${imageIndex++}`;
    const externalId = getAttachmentExternalIdFromAlt(image.getAttribute('alt'));
    if (externalId != null) {
      // Картинка обозначает вложение: показывается само вложение, а не найденное — ничем
      const attachment = attachmentsByExternalId.get(externalId);
      return attachment ? (
        <span key={key} className="my-2 block">
          <MessageAttachmentView attachment={attachment} />
        </span>
      ) : null;
    }
    if (brokenImageKeys.has(imageKey)) return null;
    return (
      <ContentImage
        key={key}
        imageProps={getElementProps(image)}
        onOpen={onImageOpen}
        onError={() => onImageError(getBrokenContentImage(image, imageKey))}
      />
    );
  };

  const renderChildren = (parent: Node): ReactNode[] => {
    const children: ReactNode[] = [];
    for (const [index, child] of Array.from(parent.childNodes).entries()) {
      if (forwarded && child === forwarded.block) {
        // Всё, что идёт за блоком пересланного письма, — тело письма: оно внутри его компонента
        children.push(<Fragment key={index}>{forwarded.node}</Fragment>);
        break;
      }
      children.push(renderNode(child, index));
    }
    return children;
  };

  const renderNode = (node: Node, key: number): ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) return isTableLayoutWhitespace(node) ? null : node.textContent;
    if (!isElementNode(node)) return null;

    const tag = node.tagName.toLowerCase();
    if (tag === 'img') return renderImage(node, key);

    // Абзац показывается блоком: на его месте могут встать вложение и пересланное письмо, а им
    // внутри абзаца не место. Отступов у абзаца в оформлении приложения и так нет
    const elementType = tag === 'p' ? 'div' : tag;
    const props = { ...getElementProps(node), key };
    return isVoidElement(node)
      ? createElement(elementType, props)
      : createElement(elementType, props, ...renderChildren(node));
  };

  return renderChildren(root);
};
